import { playheadMsAt, Soundfont, useAudioEngine, useInstrument } from '@tunasong/audio-ui'
import { MelodyLib } from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { useEffect, useMemo, useRef, useState } from 'react'
import { melodyInstrument } from './instrument-hint.js'

/**
 * Play a melody with its feel, and run a play head across the roll.
 *
 * The timing comes from `MelodyLib.scheduleMelody` - pure and tested to the millisecond without
 * audio - and goes straight to `engine.playScheduled`, which puts every note on the Tone transport
 * at its own absolute time. Nothing on this path rounds a millisecond. `playSequence` could not be
 * used: it walks note-to-note by duration and has no absolute time, so a 12 ms offset would be
 * silently discarded at the one place the offset means anything.
 *
 * The play head runs on the AUDIO clock, not on wall-clock time. It used to start at
 * `performance.now()` when `play()` was called, which put it several hundred ms ahead of the sound:
 * the audio is displaced from that moment by the engine's schedule lead (120 ms), the Tone context's
 * lookAhead (100 ms) and the device's output latency. Rather than subtract a constant for those -
 * two are configuration and the third is hardware - the loop reads `engine.outputTime()` against the
 * start `engine.playScheduled` reports, so every present and future offset is accounted for by
 * construction. @see `playback-clock.ts` in @tunasong/audio-ui
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.2
 */

export interface UseMelodyRollPlaybackProps {
  melody: Melody
  /** Play with `offsetMs` treated as zero, so the feel can be A/B'd. */
  quantized?: boolean
}

export interface MelodyRollPlayback {
  play: () => void
  /**
   * Play, resolving when the melody has finished sounding on the audio clock, or when stopped (Pitch
   * Trainer Phase 2, plan M6a step 6.2): the capture window opens the microphone straight after it.
   */
  playToEnd: () => Promise<void>
  stop: () => void
  isPlaying: boolean
  /** ms from the start of the melody, or null when stopped. */
  playheadMs: number | null
}

export const useMelodyRollPlayback = (props: UseMelodyRollPlaybackProps): MelodyRollPlayback => {
  const { melody, quantized = false } = props
  const engine = useAudioEngine()
  const instrumentName = melodyInstrument(melody.instrumentHint)

  /**
   * One Soundfont per instrument name, not per render: `useInstrument` registers what it is given,
   * and a fresh object each render would churn the engine's instrument table. Same shape as
   * `usePlayNotes`.
   */
  const instrument = useMemo(
    () => new Soundfont(engine.mixer.context, instrumentName),
    [engine.mixer.context, instrumentName]
  )

  /**
   * Route to THIS hook's instrument. `engine.playScheduled(events)` with no id resolves to the
   * first instrument registered on the page, so a melody on a page that also has a piano keyboard
   * would sound on whichever mounted first - and the failure is silent. @see useInstrument
   */
  const instrumentId = useInstrument('melody', instrument) ?? undefined

  const [playheadMs, setPlayheadMs] = useState<number | null>(null)
  const frame = useRef<number | null>(null)
  /** Resolves the running `playToEnd`, if any */
  const finished = useRef<(() => void) | null>(null)

  const stop = () => {
    if (frame.current !== null) {
      cancelAnimationFrame(frame.current)
      frame.current = null
    }
    setPlayheadMs(null)
    finished.current?.()
    finished.current = null
  }

  useEffect(() => stop, [])

  const play = () => {
    stop()
    const events = MelodyLib.scheduleMelody(melody, { quantized })
    if (events.length === 0) {
      return
    }
    /*
     * `ScheduledMelodyEvent.time` is ms from the start of the melody; `ScheduledNote.timeMs` is ms
     * from the schedule call. They are the same number here - playback starts at the melody's
     * start - but the two names describe different origins, so the mapping is explicit.
     *
     * `startTime` comes back as the AudioContext time that origin sounds at: the play head's zero.
     */
    const startTime = engine.playScheduled(
      events.map(event => ({
        midi: event.midi,
        velocity: event.velocity,
        timeMs: event.time,
        durationMs: event.durationMs,
      })),
      instrumentId
    )
    if (startTime === null) {
      return
    }

    const totalMs = MelodyLib.melodyDurationMs(melody, { quantized })
    const tick = () => {
      const elapsed = playheadMsAt(engine.outputTime(), startTime)
      if (elapsed >= totalMs) {
        stop()
        return
      }
      setPlayheadMs(elapsed)
      frame.current = requestAnimationFrame(tick)
    }
    /** Zero until the first note is audible - `playheadMsAt` clamps the lead away. */
    setPlayheadMs(playheadMsAt(engine.outputTime(), startTime))
    frame.current = requestAnimationFrame(tick)
  }

  const playToEnd = () =>
    new Promise<void>(resolve => {
      play()
      if (frame.current === null) {
        /** Nothing was scheduled: nothing to wait for */
        resolve()
        return
      }
      finished.current = resolve
    })

  return { play, playToEnd, stop, isPlaying: playheadMs !== null, playheadMs }
}
